import * as React from 'react'

import { cn } from '@ps/ds/lib/utils'
import { type VariantProps, cva } from 'class-variance-authority'
import { LucideIcon } from 'lucide-react'

const badgeVariants = cva(
  'inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground font-semibold',
  {
    variants: {
      variant: {
        outline: 'bg-transparent border-border',
        fill: 'border-transparent bg-neutral-200 text-foreground',
        destructive: 'border-transparent bg-red-100',
        warning: 'border-transparent bg-yellow-200',
        success: 'border-transparent bg-emerald-200',
      },
      size: {
        sm: 'px-1 py-1 text-xs rounded-md',
        md: 'px-2 py-1 text-sm rounded-md',
        iconLg: 'p-[10px] rounded-md',
        iconXl: 'p-4 rounded-xl',
        icon: 'p-1 rounded-md',
      },
      rounded: {
        true: 'rounded-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'outline',
      size: 'sm',
      rounded: false,
    },
  },
)

// Icon configuration based on badge variant and size
const iconConfig = {
  size: {
    sm: 16,
    md: 20,
    iconLg: 20,
    iconXl: 32,
    icon: 16,
  },
  variant: {
    outline: 'text-alabaster-600',
    fill: 'text-muted-foreground',
    destructive: 'text-destructive-foreground',
    warning: 'text-yellow-700',
    success: 'text-emerald-500',
  },
} as const

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  icon?: LucideIcon
  iconProps?: React.ComponentProps<LucideIcon>
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    { className, variant = 'outline', size = 'sm', rounded, icon: Icon, iconProps, ...props },
    ref,
  ) => {
    // Get icon size and color from config based on badge variant and size
    const iconSize = iconConfig.size[size ?? 'sm']
    const iconColorClass = iconConfig.variant[variant ?? 'outline']
    const isIconOnly = size === 'icon' || size === 'iconLg' || size === 'iconXl'

    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant, size, rounded }), className)}
        data-testid="badge"
        {...props}>
        {Icon && (
          <Icon
            size={iconSize}
            className={cn(iconColorClass, iconProps?.className)}
            data-testid="badge-icon"
            {...iconProps}
          />
        )}
        <span className={cn(isIconOnly ? 'sr-only' : 'px-1')}>{props.children}</span>
      </div>
    )
  },
)

Badge.displayName = 'Badge'

export { Badge, badgeVariants }
